import { useCallback, useEffect, useRef, useState } from "react"

import type { UpdateAiChatSettings } from "@productlane/zero/types"
import { defaultAiChatWelcomeMessage } from "@productlane/lib/src/ai-chat-defaults"
import {
	defaultCloseLoopProjectTemplate,
	defaultCloseLoopTemplate,
	defaultCloseLoopUpvoteTemplate,
} from "@productlane/lib/src/close-loop-template"
import { starter } from "@productlane/lib/src/plans-client"
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	SelectTrigger,
	Switch,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { docsDefaultExampleQuestions } from "@/constants"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { useRequiredPlans } from "@/lib/plans"
import { useAutosaveText } from "@/lib/use-autosave-text"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { CloseLoopSummaryCard } from "./close-loop-summary-card"
import { CloseLoopTemplateEditor } from "./close-loop-template-editor"

type CloseLoopPatch = Pick<
	UpdateAiChatSettings,
	| "closeLoopAutoSend"
	| "closeLoopTemplate"
	| "closeLoopUpvoteTemplate"
	| "closeLoopIssueStatusIds"
	| "closeLoopProjectTemplate"
	| "closeLoopProjectStatusIds"
>

interface ShippedStatus {
	id: string
	name: string
	teamName?: string
}

function ShippedStatusPicker({
	statuses,
	loading,
	chosenIds,
	onToggle,
}: {
	statuses: ShippedStatus[] | null | undefined
	loading: boolean
	chosenIds: string[]
	onToggle: (statusId: string, checked: boolean) => void
}) {
	const label =
		chosenIds.length === 0
			? "Any completed"
			: chosenIds.length === 1
				? (statuses?.find((s) => s.id === chosenIds[0])?.name ?? "1 status")
				: `${chosenIds.length} statuses`

	return (
		<DropdownMenu
			trigger={
				<SelectTrigger
					disabled={loading || !statuses?.length}
					className="w-40 dark:bg-gray-700/70"
				>
					{label}
				</SelectTrigger>
			}
			contentProps={{ align: "end" }}
			className="max-h-72 overflow-y-auto"
		>
			{(statuses ?? []).map((status) => (
				<DropdownMenuCheckboxItem
					key={status.id}
					checked={chosenIds.includes(status.id)}
					onSelect={(e) => e.preventDefault()}
					onCheckedChange={(checked) => onToggle(status.id, checked === true)}
				>
					<span className="truncate">
						{status.name}
						{status.teamName && (
							<span className="ml-1.5 text-tertiary">{status.teamName}</span>
						)}
					</span>
				</DropdownMenuCheckboxItem>
			))}
		</DropdownMenu>
	)
}

const toggled = (ids: string[], id: string, checked: boolean) =>
	checked ? [...ids, id] : ids.filter((existing) => existing !== id)

// The default is stored as null, so a later change to the default reaches the workspace.
const toStored = (value: string, fallback: string) =>
	value.trim().length === 0 || value.trim() === fallback.trim() ? null : value

export function AiCloseLoopSection() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const { passes: hasRequiredPlan } = useRequiredPlans(starter.andUp)
	const createdSettingsIdRef = useRef<string | null>(null)

	const settings = workspace?.aiChatSettings
	const autoSend = settings?.closeLoopAutoSend ?? false
	const chosenStatusIds = settings?.closeLoopIssueStatusIds ?? []
	const chosenProjectStatusIds = settings?.closeLoopProjectStatusIds ?? []

	const [template, setTemplate] = useState(defaultCloseLoopTemplate)
	const [projectTemplate, setProjectTemplate] = useState(
		defaultCloseLoopProjectTemplate,
	)
	const [upvoteTemplate, setUpvoteTemplate] = useState(
		defaultCloseLoopUpvoteTemplate,
	)
	const [dirty, setDirty] = useState({
		template: false,
		project: false,
		upvote: false,
	})

	const initializedRef = useRef(false)
	useEffect(() => {
		if (!settings || initializedRef.current) return
		initializedRef.current = true
		setTemplate(settings.closeLoopTemplate ?? defaultCloseLoopTemplate)
		setProjectTemplate(
			settings.closeLoopProjectTemplate ?? defaultCloseLoopProjectTemplate,
		)
		setUpvoteTemplate(
			settings.closeLoopUpvoteTemplate ?? defaultCloseLoopUpvoteTemplate,
		)
	}, [settings])

	const { data: statuses, isLoading: statusesLoading } =
		api.linear.closeLoopShippedStatuses.useQuery(undefined, {
			enabled: hasRequiredPlan,
			staleTime: 5 * 60 * 1000,
		})
	const { data: projectStatuses, isLoading: projectStatusesLoading } =
		api.linear.closeLoopShippedProjectStatuses.useQuery(undefined, {
			enabled: hasRequiredPlan,
			staleTime: 5 * 60 * 1000,
		})

	const save = useCallback(
		async (patch: CloseLoopPatch) => {
			if (!workspace) return
			const settingsId =
				workspace.aiChatSettings?.id ?? createdSettingsIdRef.current
			if (settingsId) {
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.update({ id: settingsId, ...patch }),
					),
				)
				return
			}
			const id = generateId()
			createdSettingsIdRef.current = id
			await mutateOk(
				zero.mutate(
					mutators.aiChatSettings.create({
						id,
						workspaceId: workspace.id,
						welcomeMessage: defaultAiChatWelcomeMessage(workspace.name),
						exampleQuestions: docsDefaultExampleQuestions,
						isEnabled: false,
						...patch,
					}),
				),
			)
		},
		[workspace, zero],
	)

	const saveOrToast = useCallback(
		async (patch: CloseLoopPatch, failTitle: string) => {
			try {
				await save(patch)
				return true
			} catch (error) {
				captureError(error, {
					domain: "ai-chat",
					action: "update close-loop settings",
					workspaceId: workspace?.id,
				})
				toast.error({
					title: failTitle,
					description:
						error instanceof Error ? error.message : "An error occurred",
				})
				return false
			}
		},
		[save, workspace?.id],
	)

	const flushTemplate = useAutosaveText({
		value: template,
		persistedValue: settings?.closeLoopTemplate ?? defaultCloseLoopTemplate,
		onSave: (value) =>
			void saveOrToast(
				{ closeLoopTemplate: toStored(value, defaultCloseLoopTemplate) },
				"Your follow-up message didn't save",
			),
		enabled: dirty.template,
	})

	const flushProjectTemplate = useAutosaveText({
		value: projectTemplate,
		persistedValue:
			settings?.closeLoopProjectTemplate ?? defaultCloseLoopProjectTemplate,
		onSave: (value) =>
			void saveOrToast(
				{
					closeLoopProjectTemplate: toStored(
						value,
						defaultCloseLoopProjectTemplate,
					),
				},
				"Your project message didn't save",
			),
		enabled: dirty.project,
	})

	const flushUpvoteTemplate = useAutosaveText({
		value: upvoteTemplate,
		persistedValue:
			settings?.closeLoopUpvoteTemplate ?? defaultCloseLoopUpvoteTemplate,
		onSave: (value) =>
			void saveOrToast(
				{
					closeLoopUpvoteTemplate: toStored(
						value,
						defaultCloseLoopUpvoteTemplate,
					),
				},
				"Your upvote message didn't save",
			),
		enabled: dirty.upvote,
	})

	if (!workspace) return null

	const handleAutoSendToggle = async (next: boolean) => {
		const ok = await saveOrToast(
			{ closeLoopAutoSend: next },
			"Automatic follow-ups didn't update",
		)
		if (ok) {
			toast.success({
				title: next ? "Follow-ups now go to customers" : "Follow-ups are off",
			})
		}
	}

	const toggleStatus = (statusId: string, checked: boolean) =>
		void saveOrToast(
			{ closeLoopIssueStatusIds: toggled(chosenStatusIds, statusId, checked) },
			"Your released issue statuses didn't save",
		)

	const toggleProjectStatus = (statusId: string, checked: boolean) =>
		void saveOrToast(
			{
				closeLoopProjectStatusIds: toggled(
					chosenProjectStatusIds,
					statusId,
					checked,
				),
			},
			"Your released project statuses didn't save",
		)

	return (
		<>
			{hasRequiredPlan && <CloseLoopSummaryCard className="mb-4" />}
			<SettingsContainer>
				<SettingItem
					title="Send follow-ups automatically"
					description="The customer who asked hears back in their channel when the work ships."
					action={
						!hasRequiredPlan ? (
							<UpgradeButton required={starter.andUp} />
						) : (
							<Switch
								name="closeLoopAutoSend"
								checked={autoSend}
								onCheckedChange={(next) => void handleAutoSendToggle(next)}
							/>
						)
					}
				/>
				{hasRequiredPlan && (
					<>
						<SettingItem
							className="pt-3"
							title="Released issue statuses"
							description="Linear issue statuses that count as released."
							action={
								<ShippedStatusPicker
									statuses={statuses}
									loading={statusesLoading}
									chosenIds={chosenStatusIds}
									onToggle={toggleStatus}
								/>
							}
						/>
						<SettingItem
							className="pt-3"
							title="Released project statuses"
							description="Linear project statuses that count as released."
							action={
								<ShippedStatusPicker
									statuses={projectStatuses}
									loading={projectStatusesLoading}
									chosenIds={chosenProjectStatusIds}
									onToggle={toggleProjectStatus}
								/>
							}
						/>
					</>
				)}
			</SettingsContainer>

			{hasRequiredPlan && (
				<SettingsContainer className="mt-4 flex flex-col gap-4 divide-none p-4">
					<CloseLoopTemplateEditor
						label="Message for completed issue"
						kind="reported"
						value={template}
						onChange={(text) => {
							setTemplate(text)
							setDirty((d) => ({ ...d, template: true }))
						}}
						onBlur={flushTemplate}
					/>
					<CloseLoopTemplateEditor
						label="Message for completed project"
						kind="reported"
						value={projectTemplate}
						onChange={(text) => {
							setProjectTemplate(text)
							setDirty((d) => ({ ...d, project: true }))
						}}
						onBlur={flushProjectTemplate}
					/>
					<CloseLoopTemplateEditor
						label="Message for an upvote"
						kind="upvoted"
						value={upvoteTemplate}
						onChange={(text) => {
							setUpvoteTemplate(text)
							setDirty((d) => ({ ...d, upvote: true }))
						}}
						onBlur={flushUpvoteTemplate}
					/>
				</SettingsContainer>
			)}
		</>
	)
}
